import { CheckIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useI18N, useKeyPress } from "@sundae/react-hooks";
import { FractionInput, Text, Tooltip, toolkitCx } from "@sundae/ui-toolkit";
import { ADA_METADATA } from "@sundaeswap/core";
import cx from "classnames";
import { FC, memo, useCallback, useState } from "react";
import { DEFAULT_INPUT_PLACEHOLDER } from "../../../constants/exchange.constants";
import { useTasteTestContext } from "../../../pages/TasteTestDetail/context";
import useTargetPrice from "../../../pages/TasteTestDetail/hooks/useTargetPrice";

interface ITasteTestTargetPriceInputProps {
  onClose: () => void;
}

const TasteTestTargetPriceInput: FC<ITasteTestTargetPriceInputProps> = ({ onClose }) => {
  const { decimalSeparator, t, thousandSeparator } = useI18N("taste-test");
  const {
    state: { tasteTest, tasteTestPersonalization },
  } = useTasteTestContext();
  const { targetPrice, setTargetPrice } = useTargetPrice();
  const [targetPriceValue, setTargetPriceValue] = useState(targetPrice);

  const handleSaveTargetPrice = useCallback(() => {
    tasteTest?.slug && setTargetPrice({ [tasteTest.slug]: targetPriceValue });
    onClose();
  }, [tasteTest?.slug, targetPriceValue, setTargetPrice]);

  useKeyPress("Enter", handleSaveTargetPrice);
  useKeyPress("Esc", onClose);

  return (
    <div className="relative z-50 inline-flex h-5 items-center gap-2 rounded-tl-lg rounded-tr-lg motion-safe:animate-shadow-pulse">
      <div
        className="flex h-5 items-center gap-px border-b border-dotted border-b-primary"
        style={{
          borderColor: tasteTestPersonalization?.primaryColor || "",
        }}
      >
        <Text tag="span" size="sm">
          ₳
        </Text>
        <FractionInput
          autoFocus
          className={cx("h-5 w-fit max-w-lg", toolkitCx.layout.textSize.sm)}
          decimalScale={ADA_METADATA?.decimals}
          decimalSeparator={decimalSeparator}
          placeholder={DEFAULT_INPUT_PLACEHOLDER}
          thousandSeparator={thousandSeparator}
          value={targetPriceValue}
          onValueChange={({ floatValue }) => {
            if (typeof floatValue !== "undefined") setTargetPriceValue(floatValue);
          }}
        />
      </div>

      <div className="flex items-center gap-2 motion-safe:animate-slide-up">
        <div role="button" className="cursor-pointer" onClick={handleSaveTargetPrice}>
          <Tooltip
            trigger={
              <div>
                <CheckIcon className={cx("h-3 w-3", toolkitCx.layout.text.success)} />
              </div>
            }
            tooltipLabel={t("targetPrice", { context: "set" })}
          />
        </div>
        <div role="button" className="cursor-pointer" onClick={onClose}>
          <XMarkIcon className={cx("h-3 w-3", toolkitCx.layout.text.error)} />
        </div>
      </div>
    </div>
  );
};

export default memo(TasteTestTargetPriceInput);
